// Core Modules
import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// External Modules
import { Observable } from 'rxjs';

// Services
import { ConfigService } from '@core/services/config/config.service';
import { HttpAdapterService } from '@core/services/http-adapter/http-adapter.service';

// Models
import { AssistantChatResponse, ConversationDetail, ConversationFeedback, ConversationHistoryPage } from './ignite-assistant.model';

/**
 * Legacy local-storage keys retired by the ME-17438 migration to the backend
 * chat-history API. Kept here (not in LocalStorageEnum) only so the one-time
 * cleanup below can find and remove them, regardless of which client id they
 * were scoped to.
 */
const LEGACY_CONVERSATIONS_KEY_PREFIX = 'igc_conversations_v1_';
const LEGACY_CLOCK_SKEW_KEY = 'igc_clock_skew_v1';

/**
 * Talks to the assistant API: sending chat turns (`POST /chat`) and reading,
 * deleting, and rating saved conversation history (`/chat/history*`).
 */
@Injectable({ providedIn: 'root' })
export class IgniteAssistantService {
  // Injectors
  private readonly httpAdapterService = inject(HttpAdapterService);
  private readonly configService = inject(ConfigService);

  // Private Members
  private readonly chatEndpoint: string = this.configService.getEndpointsByModule('appEndpoints').chat;
  private readonly chatHistoryEndpoint: string = this.configService.getEndpointsByModule('appEndpoints').chatHistory;

  constructor() {
    this.clearLegacyStorage();
  }

  /**
   * Send one turn to the assistant API. Authorization and hg-customer-code are
   * attached by the auth interceptor. Returns the thread id and the bot reply.
   */
  public sendMessage(threadId: string, message: string): Observable<AssistantChatResponse> {
    return this.httpAdapterService.post<AssistantChatResponse>(`${this.configService.getAPIBaseUrl}${this.chatEndpoint}`, { threadId, message });
  }

  /** List the caller's saved conversations, newest-updated first. */
  public listConversations(page: number, pageSize: number): Observable<ConversationHistoryPage> {
    const params = new HttpParams().set('page', page).set('page_size', pageSize);
    return this.httpAdapterService.get<ConversationHistoryPage>(`${this.configService.getAPIBaseUrl}${this.chatHistoryEndpoint}`, { params });
  }

  /** Fetch the full transcript for one saved conversation, oldest turn first. */
  public getConversation(conversationId: string): Observable<ConversationDetail> {
    return this.httpAdapterService.get<ConversationDetail>(`${this.configService.getAPIBaseUrl}${this.chatHistoryEndpoint}/${conversationId}`);
  }

  /** Delete a saved conversation. Idempotent — resolves even if already gone. */
  public deleteConversation(conversationId: string): Observable<{ conversationId: string; deleted: number }> {
    return this.httpAdapterService.delete<{ conversationId: string; deleted: number }>(`${this.configService.getAPIBaseUrl}${this.chatHistoryEndpoint}/${conversationId}`, {});
  }

  /** Record or clear (rating: null) the caller's feedback on one turn. */
  public submitFeedback(turnId: number, rating: 'up' | 'down' | null): Observable<ConversationFeedback> {
    return this.httpAdapterService.put<ConversationFeedback>(`${this.configService.getAPIBaseUrl}${this.chatHistoryEndpoint}/${turnId}/feedback`, { rating });
  }

  /**
   * Format a conversation timestamp: time only when it is from today, otherwise
   * a short date and time (e.g. `7/1/26  2:38 PM`). Accepts both the epoch-ms
   * numbers used elsewhere in this app and the ISO date strings the chat-history
   * endpoints return.
   */
  public formatTimestamp(timestamp: number | string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const hours = date.getHours() % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const meridiem = date.getHours() < 12 ? 'AM' : 'PM';
    const time = `${hours}:${minutes} ${meridiem}`;
    if (date.toDateString() === now.toDateString()) return time;
    return `${date.getMonth() + 1}/${date.getDate()}/${String(date.getFullYear()).slice(2)}  ${time}`;
  }

  /** One-time cleanup of the retired localStorage-based history, across every client it was ever scoped to. */
  private clearLegacyStorage(): void {
    try {
      const legacyKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key === LEGACY_CLOCK_SKEW_KEY || key.startsWith(LEGACY_CONVERSATIONS_KEY_PREFIX))) legacyKeys.push(key);
      }
      legacyKeys.forEach((key) => localStorage.removeItem(key));
    } catch {
      // Storage may be unavailable (private mode / blocked); cleanup is best-effort.
    }
  }
}
